import React from 'react';
import Modal from '../../../../Presentation/components/ui/Modal';
import {StoreDeliveryTransaction} from '../../models/storeDeliveries.model';
import {AppTitle, AppText, AppLabelText} from '../../../../Presentation/components/ui/Typography';
import {Package, Barcode, X} from 'lucide-react';

interface StoreDeliveryTransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: StoreDeliveryTransaction | null;
  loading: boolean;
  error: string | null;
}

export const StoreDeliveryTransactionDetailsModal: React.FC<StoreDeliveryTransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
  loading,
  error
}) => {
  if (loading) {
    return (
      <Modal open={isOpen} onClose={onClose} title="تفاصيل المعاملة" width="max-w-3xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          <span className="mr-3 text-gray-600">جاري التحميل...</span>
        </div>
      </Modal>
    );
  }

  if (error) {
    const isNotFound = error.includes('not found') || error.includes('Delivery not found') || error.includes('لم يتم العثور');
    return (
      <Modal open={isOpen} onClose={onClose} title="تفاصيل المعاملة" width="max-w-3xl">
        <div className={isNotFound ? 'bg-gray-50 border border-gray-200 rounded-lg p-6' : 'bg-red-50 border border-red-200 rounded-lg p-6'}>
          <div className="flex items-center gap-3">
            <div className={isNotFound ? 'text-gray-500' : 'text-red-600'}>⚠️</div>
            <div>
              <AppLabelText className={isNotFound ? 'text-gray-900' : 'text-red-900'}>
                {isNotFound ? 'لم يتم العثور على التسليم' : 'خطأ في التحميل'}
              </AppLabelText>
              {!isNotFound && <p className="text-sm text-red-700 mt-1">{error}</p>}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  if (!transaction) {
    return (
      <Modal open={isOpen} onClose={onClose} title="تفاصيل المعاملة" width="max-w-3xl">
        <div className="text-center py-12">
          <AppText className="text-gray-500">لا توجد بيانات متاحة</AppText>
        </div>
      </Modal>
    );
  }

  const getActionTypeLabel = (type: string) => {
    switch (type) {
    case 'delivery': return 'تسليم';
    case 'return': return 'إرجاع';
    case 'adjustment': return 'تسوية';
    default: return type;
    }
  };

  const getSourceTypeLabel = (type: string) => {
    switch (type) {
    case 'warehouse': return 'مستودع';
    case 'marketer': return 'مسوق';
    case 'provider': return 'مزود';
    case 'system': return 'نظام';
    default: return type;
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="تفاصيل المعاملة" width="max-w-3xl">
      <div className="space-y-6">
        {/* Transaction Header */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <AppLabelText className="text-gray-600">التاريخ والوقت</AppLabelText>
              <AppText className="font-semibold">{transaction.date} - {transaction.time}</AppText>
            </div>
            <div>
              <AppLabelText className="text-gray-600">رقم المعاملة</AppLabelText>
              <AppText className="font-semibold">{transaction.transactionId}</AppText>
            </div>
            <div>
              <AppLabelText className="text-gray-600">نوع العملية</AppLabelText>
              <AppText className="font-semibold">{getActionTypeLabel(transaction.actionType)}</AppText>
            </div>
            <div>
              <AppLabelText className="text-gray-600">المصدر</AppLabelText>
              <AppText className="font-semibold">
                {getSourceTypeLabel(transaction.sourceType)}
                {transaction.sourceName && ` - ${transaction.sourceName}`}
              </AppText>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div>
          <AppTitle className="mb-4">المنتجات ({transaction.itemsCount})</AppTitle>
          {transaction.items && transaction.items.length > 0 ? (
            <div className="space-y-3">
              {transaction.items.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-gray-400" />
                        <AppText className="font-semibold text-gray-900">{item.productName}</AppText>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Barcode className="w-3 h-3" />
                          <span dir="ltr">{item.productBarcode}</span>
                        </div>
                        <div>
                          الكمية: <span className="font-semibold text-gray-900">{item.quantity}</span>
                        </div>
                      </div>
                      {item.hasSerial && item.serialNumbers && item.serialNumbers.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <AppLabelText className="text-gray-600 mb-2 block">أرقام السيريال ({item.serialNumbers.length})</AppLabelText>
                          <div className="flex flex-wrap gap-2">
                            {item.serialNumbers.map((serial, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-brand-50 text-brand-700 rounded text-xs font-mono"
                                dir="ltr"
                              >
                                {serial}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <AppText className="text-gray-500">لا توجد منتجات في هذه المعاملة</AppText>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-brand-50 rounded-lg p-4 border border-brand-200">
          <div className="flex items-center justify-between">
            <AppLabelText className="text-brand-900">الكمية الإجمالية</AppLabelText>
            <AppTitle className="text-brand-900">{transaction.totalQuantity} قطعة</AppTitle>
          </div>
        </div>
      </div>
    </Modal>
  );
};
